/**
 * Aurified Global Entrance Animation Controller
 * Lightweight, accessible, dual-mode (IntersectionObserver + passive container scroll)
 * Ensures complete sequence finishes well under 3.0s ceiling.
 */
(function () {
  'use strict';

  // 1. Accessibility: Check prefers-reduced-motion
  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return;
  }

  // 2. Mark document ready for animation styles
  document.documentElement.classList.add('animations-ready');

  const isDesignMode = Boolean(window.Shopify && window.Shopify.designMode);

  // 3. Single-instance IntersectionObserver (runs once per element, unobserves immediately)
  const observerOptions = {
    root: null,
    rootMargin: '0px 0px -40px 0px',
    threshold: 0.08
  };

  const entranceObserver = new IntersectionObserver(function (entries, observer) {
    entries.forEach(function (entry) {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-animated');
        observer.unobserve(entry.target);
      }
    });
  }, observerOptions);

  /**
   * Fast viewport check for elements inside scroll containers (.page-wrapper / window)
   */
  function checkVisibleElements() {
    const unAnimated = document.querySelectorAll('[data-animate]:not(.is-animated)');
    if (unAnimated.length === 0) return;
    const vh = window.innerHeight;
    unAnimated.forEach(function (el) {
      const rect = el.getBoundingClientRect();
      if (rect.top < vh - 40 && rect.bottom > 0) {
        el.classList.add('is-animated');
        entranceObserver.unobserve(el);
      }
    });
  }

  /**
   * Registers elements for smooth luxury entrance animations
   * @param {Element|Document} root
   */
  function registerAnimations(root) {
    root = root || document;

    if (isDesignMode) {
      root.querySelectorAll('[data-animate]').forEach(function (el) {
        el.classList.add('is-animated');
      });
      return;
    }

    // Targets and their animation direction
    const targets = [
      // Section headers, collection titles & editorial headlines (slide left)
      {
        selector: '.section-resource-list__header, .main-collection-title, .ai-editorial__headline, .ai-editorial-hero__heading, .blog-post-card__title, .main-search__title, .editorial-category-grid__header',
        type: 'slide-left'
      },

      // Product & collection cards, blog list items, category cards (slide up)
      {
        selector: '.resource-list--grid > .resource-list__item, .product-grid > .product-grid__item, .collection-wrapper > .collection-card, .blog-posts-list__item, .editorial-category-card',
        type: 'slide-up'
      },

      // Editorial banner copy & actions
      {
        selector: '.ai-editorial__story, .ai-editorial__dek, .ai-editorial-hero__description, .ai-editorial__link, .ai-editorial-hero__button',
        type: 'slide-up'
      },

      // Editorial imagery & frames (soft fade)
      {
        selector: '.ai-editorial__visual, .ai-editorial-hero__frame',
        type: 'fade'
      },

      // Product detail page elements (title, price, description)
      {
        selector: '.product-information .product-title, .product-information .price, .product-information .product-description',
        type: 'slide-up'
      },

      // Footer blocks
      {
        selector: '.footer__block, .footer-block-list > *',
        type: 'slide-up'
      },

      // Any pre-tagged elements
      {
        selector: '[data-animate]',
        type: null
      }
    ];

    targets.forEach(function (targetGroup) {
      const elements = root.querySelectorAll(targetGroup.selector);
      elements.forEach(function (el) {
        // Safety guard: Never animate form inputs, passwords, payment controls or cart checkout
        if (
          el.tagName === 'INPUT' ||
          el.tagName === 'TEXTAREA' ||
          el.tagName === 'SELECT' ||
          el.closest('.shopify-payment-button') ||
          el.closest('form[action*="/cart/add"]') ||
          el.closest('.cart__checkout-button') ||
          el.closest('.quantity-selector')
        ) {
          return;
        }

        if (targetGroup.type && !el.hasAttribute('data-animate')) {
          el.setAttribute('data-animate', targetGroup.type);
        } else if (!el.hasAttribute('data-animate')) {
          el.setAttribute('data-animate', 'slide-up');
        }

        if (!el.classList.contains('is-animated')) {
          const rect = el.getBoundingClientRect();
          // If already inside the initial viewport, animate progressively
          if (rect.top < window.innerHeight && rect.bottom > 0) {
            el.classList.add('is-animated');
          } else {
            entranceObserver.observe(el);
          }
        }
      });
    });
  }

  // Initial registration
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      registerAnimations(document);
      bindScrollContainers();
    });
  } else {
    registerAnimations(document);
    bindScrollContainers();
  }

  function bindScrollContainers() {
    let ticking = false;
    function onScroll() {
      if (!ticking) {
        window.requestAnimationFrame(function () {
          checkVisibleElements();
          ticking = false;
        });
        ticking = true;
      }
    }

    const pageWrapper = document.querySelector('.page-wrapper');
    if (pageWrapper) {
      pageWrapper.addEventListener('scroll', onScroll, { passive: true });
    }
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  // Support Shopify section rendering API
  document.addEventListener('shopify:section:load', function (event) {
    registerAnimations(event.target);
  });

  // Fail-safe timeout: ensure all content is 100% visible before 3.0 seconds maximum
  setTimeout(function () {
    document.querySelectorAll('[data-animate]:not(.is-animated)').forEach(function (el) {
      el.classList.add('is-animated');
    });
  }, 2200);
})();
